import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import type { AssignOnDemandDaySpec, TrainingBlock, TrainingProgramActivation } from '../../types/api'
import type { TrainingTagDto } from '../../api/trainingBlockApi'
import { BLOCK_TAG_CATEGORIES, flattenTrainingTags } from '../../api/trainingBlockApi'
import { trainingBlockApi } from '../../api'
import { trainingProgramActivationApi } from '../../api/trainingProgramActivationApi'
import type { ApiError } from '../../api/client'
import { Button, DashedAddButton } from '../ui'
import { useTranslation } from '../../i18n/useTranslation'
import { useTheme } from '../../hooks/useTheme'
import { BlockItemsEditor } from './BlockItemsEditor'
import { BlockIndexLabel } from './BlockIndexLabel'
import { countExercisesInItems, emptyItem, itemsToApi, type ItemRow } from './blockItemHelpers'
import { TagCategoryFilter } from './TagCategoryFilter'
import {
  emptyTagCategoryFilterState,
  matchesTagCategoryFilters,
  type TagCategoryFilterState,
} from './tagCategoryFilterUtils'

const WEEK_PRESETS = [1, 2, 4, 6, 8] as const

function todayIso(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function addWeeksIso(iso: string, weeks: number): string {
  const d = new Date(`${iso}T12:00:00`)
  d.setDate(d.getDate() + weeks * 7)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function weeksBetweenIso(startIso: string, endIso: string): number {
  const start = new Date(`${startIso}T12:00:00`)
  const end = new Date(`${endIso}T12:00:00`)
  const days = Math.round((end.getTime() - start.getTime()) / 86_400_000)
  return Math.max(1, Math.min(52, Math.round(days / 7)))
}

function nearestPresetWeeks(weeks: number): number {
  return WEEK_PRESETS.reduce((best, w) =>
    Math.abs(w - weeks) < Math.abs(best - weeks) ? w : best
  )
}

type DayDraft =
  | { kind: 'existing'; block: TrainingBlock }
  | { kind: 'new'; name: string; items: ItemRow[] }

interface Props {
  traineeId: number
  onClose: () => void
  onAssigned: (activation: TrainingProgramActivation) => void
}

export default function OnDemandAssignComposer({ traineeId, onClose, onAssigned }: Props) {
  const theme = useTheme()
  const { t, translate } = useTranslation()

  const [days, setDays] = useState<DayDraft[]>([])
  const [blocks, setBlocks] = useState<TrainingBlock[]>([])
  const [availableTags, setAvailableTags] = useState<TrainingTagDto[]>([])
  const [pickerIndex, setPickerIndex] = useState<number | 'append' | null>(null)
  const [creatingIndex, setCreatingIndex] = useState<number | 'append' | null>(null)
  const [draftName, setDraftName] = useState('')
  const [draftItems, setDraftItems] = useState<ItemRow[]>([emptyItem()])
  const [activeFilters, setActiveFilters] = useState<TagCategoryFilterState>(emptyTagCategoryFilterState())
  const [startDate, setStartDate] = useState(todayIso)
  const [weeks, setWeeks] = useState(4)
  const [endDate, setEndDate] = useState(() => addWeeksIso(todayIso(), 4))
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const itemLabels = {
    itemsLabel: t.createTrainingBlock.exercisesLabel,
    addExercise: t.createTrainingBlock.addExercise,
    addToGroup: t.createTrainingBlock.addToGroup,
    notePlaceholder: t.createTrainingBlock.notePlaceholder,
    addNote: t.createTrainingBlock.addNote,
    changeNote: t.createTrainingBlock.changeNote,
    doneNote: t.createTrainingBlock.doneNote,
    exerciseNamePlaceholder: t.createTrainingBlock.exerciseNamePlaceholder,
    groupLabel: t.createTrainingBlock.groupLabel,
  }

  useEffect(() => {
    Promise.all([trainingBlockApi.getBlocks(), trainingBlockApi.getTags()])
      .then(([blockList, tags]) => {
        setBlocks(blockList)
        setAvailableTags(flattenTrainingTags(tags))
      })
      .catch(() => {})
  }, [])

  const usedTagKeys = useMemo(() => {
    const keys = new Set<string>()
    blocks.forEach(b => b.tags?.forEach(tag => keys.add(tag)))
    return keys
  }, [blocks])

  const filteredBlocks = useMemo(
    () => blocks.filter(b => matchesTagCategoryFilters(b.tags, activeFilters)),
    [blocks, activeFilters]
  )

  const sectionLabel: CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.text.muted,
    marginBottom: 12,
  }

  function defaultBlockName(index: number | 'append'): string {
    const n = index === 'append' ? days.length + 1 : index + 1
    return translate('trainingPrograms.assignProgram.onDemand.defaultBlockName', { n })
  }

  function handleStartDateChange(value: string) {
    setStartDate(value)
    setEndDate(addWeeksIso(value, weeks))
    setError(null)
  }

  function handleWeeksChange(nextWeeks: number) {
    const clamped = Math.max(1, Math.min(52, nextWeeks))
    setWeeks(clamped)
    setEndDate(addWeeksIso(startDate, clamped))
    setError(null)
  }

  function handleEndDateChange(value: string) {
    if (value < startDate) {
      setEndDate(value)
      setError(t.trainingPrograms.assignProgram.endBeforeStart)
      return
    }
    setError(null)
    const snapped = nearestPresetWeeks(weeksBetweenIso(startDate, value))
    setWeeks(snapped)
    setEndDate(addWeeksIso(startDate, snapped))
  }

  function openCreate(index: number | 'append') {
    setCreatingIndex(index)
    setDraftName(defaultBlockName(index))
    setDraftItems([emptyItem()])
    setError(null)
  }

  function confirmCreateBlock() {
    if (creatingIndex == null) return
    const name = draftName.trim() || defaultBlockName(creatingIndex)
    const payloadItems = itemsToApi(draftItems)
    if (payloadItems.length === 0) {
      setError(t.trainingPrograms.assignProgram.onDemand.error.exerciseRequired)
      return
    }
    const draft: DayDraft = {
      kind: 'new',
      name,
      items: draftItems,
    }
    setDays(prev => {
      if (creatingIndex === 'append') return [...prev, draft]
      return prev.map((d, i) => (i === creatingIndex ? draft : d))
    })
    setCreatingIndex(null)
    setError(null)
  }

  function pickExistingBlock(block: TrainingBlock) {
    if (pickerIndex == null) return
    const draft: DayDraft = { kind: 'existing', block }
    setDays(prev => {
      if (pickerIndex === 'append') return [...prev, draft]
      return prev.map((d, i) => (i === pickerIndex ? draft : d))
    })
    setPickerIndex(null)
  }

  function removeDay(index: number) {
    setDays(prev => prev.filter((_, i) => i !== index))
  }

  async function handleAssign() {
    if (days.length === 0) {
      setError(t.trainingPrograms.assignProgram.onDemand.error.daysRequired)
      return
    }
    if (endDate < startDate) {
      setError(t.trainingPrograms.assignProgram.endBeforeStart)
      return
    }

    const daySpecs: AssignOnDemandDaySpec[] = days.map(day => {
      if (day.kind === 'existing') {
        return { blockId: day.block.id }
      }
      return {
        newBlock: {
          name: day.name,
          items: itemsToApi(day.items),
        },
      }
    })

    setLoading(true)
    setError(null)
    try {
      const activation = await trainingProgramActivationApi.assignOnDemand({
        traineeId,
        durationWeeks: weeks,
        startDate,
        days: daySpecs,
      })
      onAssigned(activation)
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || t.trainingPrograms.assignProgram.error.assignFailed)
    } finally {
      setLoading(false)
    }
  }

  if (pickerIndex != null) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col" style={{ backgroundColor: theme.bgColor }}>
        <div className="flex items-center gap-3 px-4 py-3 shrink-0" style={{ borderBottom: `1px solid ${theme.cardBorder}` }}>
          <button type="button" onClick={() => setPickerIndex(null)} className="p-2 -ml-2 rounded-xl" style={{ color: theme.text.primary }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>
          <h2 className="text-lg font-semibold" style={{ color: theme.text.primary }}>
            {t.trainingPrograms.assignProgram.onDemand.pickBlock}
          </h2>
        </div>
        {usedTagKeys.size > 0 && (
          <div className="px-4 py-3 shrink-0" style={{ borderBottom: `1px solid ${theme.cardBorder}` }}>
            <TagCategoryFilter
              tags={availableTags}
              usedTagKeys={usedTagKeys}
              categories={[...BLOCK_TAG_CATEGORIES]}
              value={activeFilters}
              onChange={setActiveFilters}
              bleed={false}
            />
          </div>
        )}
        <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-2">
          {filteredBlocks.length === 0 ? (
            <p className="text-sm text-center py-10" style={{ color: theme.text.muted }}>
              {t.trainingPrograms.assignProgram.onDemand.noSavedBlocks}
            </p>
          ) : (
            filteredBlocks.map(block => (
              <button
                key={block.id}
                type="button"
                onClick={() => pickExistingBlock(block)}
                className="rounded-2xl p-4 text-left"
                style={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.cardBorder}` }}
              >
                <p className="font-semibold text-sm" style={{ color: theme.text.primary }}>{block.name}</p>
                <p className="text-xs mt-1" style={{ color: theme.text.muted }}>
                  {t.trainingBlocks.exerciseCount_other.replace('{{count}}', String(countExercisesInItems(block.items)))}
                </p>
              </button>
            ))
          )}
        </div>
      </div>
    )
  }

  if (creatingIndex != null) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col" style={{ backgroundColor: theme.bgColor }}>
        <div className="flex items-center gap-3 px-4 py-3 shrink-0" style={{ borderBottom: `1px solid ${theme.cardBorder}` }}>
          <button type="button" onClick={() => setCreatingIndex(null)} className="p-2 -ml-2 rounded-xl" style={{ color: theme.text.primary }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>
          <h2 className="text-lg font-semibold" style={{ color: theme.text.primary }}>
            {t.trainingPrograms.assignProgram.onDemand.createBlock}
          </h2>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-5 space-y-4">
          <input
            type="text"
            value={draftName}
            onChange={e => setDraftName(e.target.value)}
            placeholder={t.trainingPrograms.assignProgram.onDemand.defaultBlockName.replace('{{n}}', '1')}
            className="w-full rounded-xl px-4 py-3 text-base outline-none"
            style={{ backgroundColor: theme.inputBg, color: theme.text.primary, border: `1px solid ${theme.inputBorder}` }}
          />
          <BlockItemsEditor
            items={draftItems}
            onChange={setDraftItems}
            textColor={theme.text.primary}
            bgColor={theme.bgColor}
            labels={itemLabels}
          />
          {error && (
            <div className="rounded-xl p-3 bg-red-500/10 border border-red-500/25">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}
        </div>
        <div className="px-4 pb-8 pt-3 shrink-0" style={{ borderTop: `1px solid ${theme.cardBorder}` }}>
          <Button type="button" variant="primary" size="lg" fullWidth onClick={confirmCreateBlock}>
            {t.trainingPrograms.assignProgram.onDemand.addBlock}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ backgroundColor: theme.bgColor }}>
      <div className="flex items-center gap-3 px-4 py-3 shrink-0" style={{ borderBottom: `1px solid ${theme.cardBorder}` }}>
        <button type="button" onClick={onClose} className="p-2 -ml-2 rounded-xl" style={{ color: theme.text.primary }} aria-label="Back">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6"/>
          </svg>
        </button>
        <h2 className="text-lg font-semibold" style={{ color: theme.text.primary }}>
          {t.trainingPrograms.assignProgram.onDemand.title}
        </h2>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-5">
        <p style={sectionLabel}>{t.trainingPrograms.assignProgram.onDemand.daysLabel}</p>
        <div className="flex flex-col gap-2 mb-4">
          {days.map((day, index) => (
            <div
              key={index}
              className="rounded-2xl p-4 flex items-start gap-3"
              style={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.cardBorder}` }}
            >
              <div className="flex-1 min-w-0">
                <BlockIndexLabel index={index + 1} className="block mb-0.5" />
                <p className="text-sm font-semibold truncate" style={{ color: theme.text.primary }}>
                  {day.kind === 'existing' ? day.block.name : day.name}
                </p>
                <p className="text-xs mt-0.5" style={{ color: theme.text.muted }}>
                  {day.kind === 'existing'
                    ? t.trainingPrograms.assignProgram.onDemand.fromLibrary
                    : t.trainingPrograms.assignProgram.onDemand.newBlockBadge}
                </p>
              </div>
              <button
                type="button"
                onClick={() => removeDay(index)}
                className="p-2.5 rounded-xl shrink-0 -mr-1"
                style={{ color: '#ef4444' }}
                aria-label={t.trainingPrograms.assignProgram.onDemand.removeDay}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                  <path d="M10 11v6"/><path d="M14 11v6"/>
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                </svg>
              </button>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-2 mb-8">
          <DashedAddButton
            onClick={() => setPickerIndex('append')}
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
              </svg>
            }
          >
            {t.trainingPrograms.assignProgram.onDemand.pickBlock}
          </DashedAddButton>
          <DashedAddButton onClick={() => openCreate('append')}>
            {t.trainingPrograms.assignProgram.onDemand.createBlock}
          </DashedAddButton>
        </div>

        <p style={sectionLabel}>{t.trainingPrograms.assignProgram.scheduleLabel}</p>
        <div className="rounded-2xl overflow-hidden mb-4" style={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.cardBorder}` }}>
          <div className="flex items-center justify-between px-4 py-3.5" style={{ borderBottom: `1px solid ${theme.cardBorder}` }}>
            <p className="text-sm font-medium" style={{ color: theme.text.primary }}>{t.trainingPrograms.assignProgram.startDate}</p>
            <input type="date" value={startDate} onChange={e => handleStartDateChange(e.target.value)} className="text-sm font-semibold bg-transparent border-none outline-none" style={{ color: theme.text.primary, colorScheme: theme.isDark ? 'dark' : 'light' }} />
          </div>
          <div className="flex items-center justify-between px-4 py-3.5">
            <p className="text-sm font-medium" style={{ color: theme.text.primary }}>{t.trainingPrograms.assignProgram.endDate}</p>
            <input type="date" value={endDate} min={startDate} onChange={e => handleEndDateChange(e.target.value)} className="text-sm font-semibold bg-transparent border-none outline-none" style={{ color: theme.text.primary, colorScheme: theme.isDark ? 'dark' : 'light' }} />
          </div>
        </div>

        <p style={{ ...sectionLabel, marginBottom: 8 }}>{t.trainingPrograms.assignProgram.quickDuration}</p>
        <div className="flex gap-2 mb-6">
          {WEEK_PRESETS.map(w => {
            const active = weeks === w
            return (
              <button
                key={w}
                type="button"
                onClick={() => handleWeeksChange(w)}
                className="flex-1 h-11 rounded-xl text-sm font-semibold"
                style={active
                  ? { backgroundColor: theme.buttonColor, color: theme.buttonTextColor }
                  : { backgroundColor: theme.cardBg, border: `1px solid ${theme.cardBorder}`, color: theme.text.primary }}
              >
                {translate('trainingPrograms.assignProgram.weekChip', { count: w })}
              </button>
            )
          })}
        </div>

        {error && (
          <div className="rounded-xl p-3 mb-4 bg-red-500/10 border border-red-500/25">
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}
      </div>

      <div className="px-4 pb-8 pt-3 shrink-0" style={{ borderTop: `1px solid ${theme.cardBorder}` }}>
        <Button
          type="button"
          variant="primary"
          size="lg"
          fullWidth
          onClick={handleAssign}
          disabled={loading || days.length === 0 || endDate < startDate}
        >
          {loading ? t.trainingPrograms.assignProgram.assigning : t.trainingPrograms.assignProgram.assignButton}
        </Button>
      </div>
    </div>
  )
}
